import { c as _c } from "react/compiler-runtime";
import { Box, BoxProps, Card, HStack, Icon, Separator, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { PaywalledAction } from '@src/components/sco/PaywalledAction';
import { PITCH_DECK_UPGRADE_PRODUCT_ID } from '@src/components/sco/PitchDeckAuditor/model/access';
import { PitchSlideView } from '@src/components/sco/PitchDeckAuditor/model/types';
import { SectionHeading } from '@src/components/sco/PitchDeckAuditor/shared/SectionHeading';
import { LuCheck, LuMessageSquareQuote, LuPencilLine, LuThumbsDown, LuThumbsUp, LuTriangleAlert } from 'react-icons/lu';

/** A static (non-animated) bar standing in for redacted audit copy. */
function SkeletonLine(t0) {
  const $ = _c(6);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      width: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const width = t1 === undefined ? "100%" : t1;
  let t2;
  if ($[3] !== props || $[4] !== width) {
    t2 = <Box bg="bg.emphasized" borderRadius="l2" height="3" width={width} {...props} />;
    $[3] = props;
    $[4] = width;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}

/**
 * Shown in place of the audit body on a paywalled slide. The slide header still renders above this
 * (in SlideDetail); here we surface the upgrade CTA followed by a dimmed, skeleton-text mock of the
 * real analysis blocks (What you wrote / Top Problems / Suggested rewrite / Why the rewrite works) so
 * the visitor can see the shape of what they'd unlock without exposing the actual content.
 */
export function LockedSlideAudit(t0) {
  const $ = _c(18);
  const {
    slide
  } = t0;
  const t1 = `Slide ${slide.position} is part of the full audit. Upgrade to read the rest of this deck.`;
  let t2;
  if ($[0] !== t1) {
    t2 = <PaywalledAction contentPermission="canAccessPitchDeckAuditor" title={t1} upgradeCtaLabel="Unlock the Full Audit" upgradeProductId={PITCH_DECK_UPGRADE_PRODUCT_ID} variant="boxed" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SectionHeading icon={LuMessageSquareQuote} textStyle="lg">What you wrote</SectionHeading>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <VStack align="stretch" gap={2}><SkeletonLine /><SkeletonLine width="94%" /><SkeletonLine width="72%" /></VStack>;
    t5 = <Separator />;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      base: 1,
      lg: 2
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <HStack color="teal.fg" gap={2} mb={1.5}><Icon as={LuThumbsUp} boxSize={4} /><Text textStyle="labelXs">What works</Text></HStack>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Card.Root borderTopColor="teal.solid" borderTopWidth="3px" size="sm"><Card.Body>{t7}<VStack align="stretch" gap={2}><SkeletonLine /><SkeletonLine width="80%" /></VStack></Card.Body></Card.Root>;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <HStack color="red.fg" gap={2} mb={1.5}><Icon as={LuThumbsDown} boxSize={4} /><Text textStyle="labelXs">What needs work</Text></HStack>;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Card.Root variant="subtle"><Card.Body gap={4} padding={4}>{t3}{t4}{t5}<SimpleGrid columns={t6} gap={4}>{t8}<Card.Root borderTopColor="red.solid" borderTopWidth="3px" size="sm"><Card.Body>{t9}<VStack align="stretch" gap={2}><SkeletonLine /><SkeletonLine width="85%" /></VStack></Card.Body></Card.Root></SimpleGrid></Card.Body></Card.Root>;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <SectionHeading icon={LuTriangleAlert} textStyle="lg">Top Problems</SectionHeading>;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <VStack align="stretch" bgColor="colorPalette.container" borderRadius="md" gap={4} padding={4}>{t11}<VStack align="stretch" gap={3}>{[0, 1, 2].map(_temp)}</VStack></VStack>;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <HStack color="orange.fg" gap={2}><Icon as={LuPencilLine} boxSize={4} /><Text fontWeight="semibold" textStyle="lg">Suggested rewrite</Text></HStack>;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Card.Root bgColor="colorPalette.card/50" variant="elevated"><Card.Body gap={3} padding={5}>{t13}<VStack align="stretch" gap={2}><SkeletonLine /><SkeletonLine width="96%" /><SkeletonLine width="88%" /><SkeletonLine width="40%" /></VStack></Card.Body></Card.Root>;
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  let t15;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <HStack color="green.fg" gap={2} mb={2}><Icon as={LuThumbsUp} boxSize={4} /><Text textStyle="labelXs">Why the rewrite works</Text></HStack>;
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  let t16;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <VStack align="stretch" aria-hidden={true} gap={6} opacity={0.5} pointerEvents="none" userSelect="none">{t10}{t12}{t14}<Card.Root size="sm" variant="subtle"><Card.Body>{t15}<VStack align="stretch" gap={1.5}>{[0, 1, 2].map(_temp2)}</VStack></Card.Body></Card.Root></VStack>;
    $[15] = t16;
  } else {
    t16 = $[15];
  }
  let t17;
  if ($[16] !== t2) {
    t17 = <VStack align="stretch" gap={6}>{t2}{t16}</VStack>;
    $[16] = t2;
    $[17] = t17;
  } else {
    t17 = $[17];
  }
  return t17;
}
function _temp2(i_0) {
  return <HStack key={i_0} align="flex-start" gap={2}><Icon as={LuCheck} boxSize={4} color="green.fg" mt={0.5} /><SkeletonLine width={i_0 === 2 ? "50%" : "100%"} /></HStack>;
}
function _temp(i) {
  return <HStack key={i} align="flex-start" gap={3}><SkeletonLine borderRadius="full" flexShrink={0} height="1.5rem" width="1.5rem" /><VStack align="stretch" flex={1} gap={1.5}><SkeletonLine /><SkeletonLine width="55%" /></VStack></HStack>;
}